/** CSV export of the full year-by-year projection (spec §19). */
import type { YearSnapshot } from '../engine/snapshot';
import { CONSTANTS_AS_OF } from '../engine/data/federal';

export function snapshotsToCsv(snapshots: YearSnapshot[], todaysDollars: boolean): string {
  const cols: [string, (s: YearSnapshot) => number | string][] = [
    ['Year', (s) => s.year],
    ['Age', (s) => Object.values(s.ages)[0] ?? ''],
    ['Wages', (s) => v(s, s.wagesIncome)],
    ['Self-employment', (s) => v(s, s.selfEmploymentIncome)],
    ['Pension', (s) => v(s, s.pensionIncome)],
    ['Annuity', (s) => v(s, s.annuityIncome)],
    ['Rental/other', (s) => v(s, s.rentalRoyaltyOtherIncome)],
    ['Social Security', (s) => v(s, s.socialSecurityGross)],
    ['RMD', (s) => v(s, s.totalRmd)],
    ['Spending', (s) => v(s, s.totalSpending)],
    ['Essential spending', (s) => v(s, s.essentialSpending)],
    ['Guaranteed income', (s) => v(s, s.guaranteedIncome)],
    ['Healthcare premiums', (s) => v(s, s.healthcarePremiums)],
    ['Healthcare OOP', (s) => v(s, s.healthcareOOP)],
    ['ACA subsidy', (s) => v(s, s.acaSubsidy)],
    ['IRMAA tier', (s) => s.irmaaTierIndex],
    ['LTC net cost', (s) => v(s, s.ltcNetCost)],
    ['Debt service', (s) => v(s, s.debtService)],
    ['Tax paid (prior yr)', (s) => v(s, s.priorYearTaxPaid)],
    ['Total outflow', (s) => v(s, s.totalOutflow)],
    ['Withdrawals', (s) => v(s, s.totalWithdrawals)],
    ['Realized LTCG', (s) => v(s, s.realizedCapitalGains)],
    ['Early-withdrawal penalty', (s) => v(s, s.earlyWithdrawalPenalty)],
    ['AGI', (s) => v(s, s.federal.agi)],
    ['Taxable income', (s) => v(s, s.federal.taxableIncome)],
    ['Deduction used', (s) => v(s, s.federal.deductionUsed)],
    ['SS taxable %', (s) => (s.federal.ss.taxablePercent * 100).toFixed(1)],
    ['Federal tax', (s) => v(s, s.federal.totalTax)],
    ['FICA', (s) => v(s, s.fica.total)],
    ['State tax (approx)', (s) => v(s, s.stateTax)],
    ['Total tax liability', (s) => v(s, s.totalTaxLiability)],
    ['Marginal rate', (s) => (s.federal.marginalOrdinaryRate * 100).toFixed(0) + '%'],
    ['Effective rate', (s) => (s.federal.effectiveRate * 100).toFixed(1) + '%'],
    ['Pre-tax balance', (s) => v(s, s.balancesByFamily.pretax)],
    ['Roth balance', (s) => v(s, s.balancesByFamily.roth)],
    ['After-tax balance', (s) => v(s, s.balancesByFamily.aftertax)],
    ['HSA balance', (s) => v(s, s.balancesByFamily.hsa)],
    ['Net worth', (s) => v(s, s.netWorth)],
    ['Unfunded spending', (s) => v(s, s.unfundedSpending)],
    ['Depleted', (s) => (s.depleted ? 'YES' : '')],
  ];

  function v(s: YearSnapshot, n: number): number {
    const x = todaysDollars ? n / s.cumulativeInflationFactor : n;
    return Math.round(x);
  }

  const header = cols.map(([name]) => csvEscape(name)).join(',');
  const rows = snapshots.map((s) => cols.map(([, fn]) => csvEscape(String(fn(s)))).join(','));
  const meta = `# Retirement Planner projection export. Dollars: ${todaysDollars ? "today's" : 'nominal'}. Tax constants as of ${CONSTANTS_AS_OF}.`;
  return [meta, header, ...rows].join('\r\n');
}

function csvEscape(s: string): string {
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadText(filename: string, text: string, mime = 'text/csv'): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
